import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';

/**
 * TOPUP SETTINGS — the rules, the messages, and who is a supervisor.
 *
 * Three things an operator changes without a deploy:
 *   - what a topup is worth and which payment methods are offered
 *   - which status changes send an SMS, and what it says
 *   - which members may pass their shopping balance down their own tree
 *
 * The templates are plain text with {tokens}; the token list is served by the
 * backend so it can never drift from what the sender actually fills in.
 */

const METHOD_LABELS = {
  cash: 'Cash',
  mobile_banking: 'Mobile banking',
  bank_transfer: 'Bank transfer',
  other: 'Other',
};

const EVENTS = [
  { flag: 'onSubmitted', template: 'submitted', label: 'Request received' },
  { flag: 'onVerified', template: 'verified', label: 'Payment verified by Accounts' },
  { flag: 'onApproved', template: 'approved', label: 'Approved — BDT added to Shopping Balance' },
  { flag: 'onRejected', template: 'rejected', label: 'Rejected' },
  { flag: 'onCancelled', template: 'cancelled', label: 'Cancelled by the member' },
  { flag: 'onShared', template: 'shareSent', label: 'Supervisor sent balance', second: 'shareReceived' },
];

export default function TopupSettingScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }),
    [userInfo]
  );

  const [form, setForm] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await Axios.get(api + '/api/topup/admin/settings', auth);
      setForm({
        enabled: data.enabled,
        minAmount: data.minAmount,
        maxAmount: data.maxAmount,
        maxOpenRequests: data.maxOpenRequests,
        methods: data.methodConfig,
        sharing: data.sharing,
        notify: data.notify,
      });
      setTokens(data.templateTokens || []);
    } catch (error) {
      toast.error(getError(error));
    }
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      await Axios.put(api + '/api/topup/admin/settings', form, auth);
      toast.success('Topup settings saved.');
      // Reload so the previews below show the messages as just saved, not as
      // they were when the page opened.
      load();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setMethod = (key, field, value) =>
    setForm((f) => ({
      ...f,
      methods: { ...f.methods, [key]: { ...f.methods[key], [field]: value } },
    }));
  const setNotify = (field, value) =>
    setForm((f) => ({ ...f, notify: { ...f.notify, [field]: value } }));
  const setTemplate = (key, value) =>
    setForm((f) => ({
      ...f,
      notify: { ...f.notify, templates: { ...f.notify.templates, [key]: value } },
    }));

  /**
   * What the message will say. Rendered here as you type using the same sample
   * values the server uses, so the placeholders stop being guesswork. Topups
   * are BDT only, so `{points}` (kept for older texts) is the same Tk figure.
   */
  const sample = useMemo(() => {
    const amount = 100000;
    return {
      name: 'Rahim Uddin',
      username: 'rahim01',
      amount,
      points: amount,
      method: 'mobile banking',
      status: 'approved',
      note: 'Payment could not be matched',
      balance: amount,
      id: 'A1B2C3D4',
      by: 'Office',
      sender: 'rahim01',
      receiver: 'karim02',
    };
  }, []);

  const renderPreview = (template) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, token) =>
      sample[token] === undefined ? match : String(sample[token])
    );

  const sendTest = async (event) => {
    if (!testPhone.trim()) {
      toast.error('Type the number to send the test to.');
      return;
    }
    if (!window.confirm(`Send this message to ${testPhone.trim()}? It costs one real message.`)) {
      return;
    }
    try {
      setTesting(event);
      const { data } = await Axios.post(
        api + '/api/topup/admin/test-message',
        { event, phone: testPhone.trim() },
        auth
      );
      toast.success(data.message);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setTesting('');
    }
  };

  /* ------------------------------------------------------------ supervisors */

  const [supervisors, setSupervisors] = useState([]);
  const [lookup, setLookup] = useState('');
  const [found, setFound] = useState(null);

  const loadSupervisors = useCallback(async () => {
    try {
      const { data } = await Axios.get(api + '/api/topup/admin/supervisors?pageSize=100', auth);
      setSupervisors(data.rows || []);
    } catch (error) {
      /* informational */
    }
  }, [auth]);

  useEffect(() => {
    loadSupervisors();
  }, [loadSupervisors]);

  const findMember = async () => {
    const username = lookup.trim();
    if (!username) return;
    try {
      // The profile-edit lookup already resolves a username to an id and is
      // open to the same staff, so it doubles as the member picker here.
      const { data } = await Axios.get(
        api + `/api/users/admin/profile-edit/user/${encodeURIComponent(username)}`,
        auth
      );
      setFound(data.user);
    } catch (error) {
      setFound(null);
      toast.error(getError(error));
    }
  };

  const setSupervisorFlag = async (userId, isSuperVisor) => {
    try {
      const { data } = await Axios.post(
        api + '/api/topup/admin/supervisor',
        { userId, isSuperVisor },
        auth
      );
      toast.success(data.message);
      loadSupervisors();
    } catch (error) {
      toast.error(getError(error));
    }
  };

  if (!form) {
    return (
      <div className="ts-page">
        <style>{CSS}</style>
        <div className="ts-card ts-muted">Loading…</div>
      </div>
    );
  }

  return (
    <div className="ts-page">
      <Helmet><title>Topup Settings</title></Helmet>
      <style>{CSS}</style>

      <div className="ts-head">
        <div>
          <h3>Topup settings</h3>
          <div className="ts-muted">
            What a topup is worth, how members pay, what they are told, and who may
            share balance downward.
          </div>
        </div>
        <Link className="ts-btn ts-btn-light" to="/admin/topup">Back to requests</Link>
      </div>

      <div className="ts-grid">
        {/* ------------------------------------------------------- the rules */}
        <div className="ts-card">
          <div className="ts-card-head">The rules</div>
          <div className="ts-body">
            <label className="ts-switch">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              <span>Members can ask for a topup</span>
            </label>
            {/* No longer a switch: verification by Accounts is always required. */}
            <div className="ts-hint">
              <b>Verification by Accounts is always required.</b> A request goes to Accounts
              (staff with the "Verify topups" permission) first, and only appears to admins
              for approval once Accounts has verified the payment.
            </div>

            <div className="ts-hint">
              Topups are BDT only: an approved BDT 100,000 topup adds BDT 100,000 to the
              member's Shopping Balance.
            </div>

            <div className="ts-two">
              <div>
                <label className="ts-label">Smallest topup (BDT)</label>
                <input
                  className="ts-input"
                  type="number"
                  value={form.minAmount}
                  onChange={(e) => set('minAmount', e.target.value)}
                />
              </div>
              <div>
                <label className="ts-label">Largest topup (BDT)</label>
                <input
                  className="ts-input"
                  type="number"
                  value={form.maxAmount}
                  onChange={(e) => set('maxAmount', e.target.value)}
                />
              </div>
            </div>
            <label className="ts-label">Open requests allowed per member</label>
            <input
              className="ts-input"
              type="number"
              value={form.maxOpenRequests}
              onChange={(e) => set('maxOpenRequests', e.target.value)}
            />
          </div>
        </div>

        {/* ---------------------------------------------------- pay methods */}
        <div className="ts-card">
          <div className="ts-card-head">Payment methods</div>
          <div className="ts-body">
            {Object.keys(METHOD_LABELS).map((key) => (
              <div className="ts-method" key={key}>
                <label className="ts-switch">
                  <input
                    type="checkbox"
                    checked={form.methods?.[key]?.enabled !== false}
                    onChange={(e) => setMethod(key, 'enabled', e.target.checked)}
                  />
                  <span><b>{METHOD_LABELS[key]}</b></span>
                </label>
                <input
                  className="ts-input"
                  placeholder="Label shown to members"
                  value={form.methods?.[key]?.label || ''}
                  onChange={(e) => setMethod(key, 'label', e.target.value)}
                />
                <textarea
                  className="ts-input"
                  rows={2}
                  placeholder="Where to send the money"
                  value={form.methods?.[key]?.instructions || ''}
                  onChange={(e) => setMethod(key, 'instructions', e.target.value)}
                />
                <label className="ts-switch">
                  <input
                    type="checkbox"
                    checked={Boolean(form.methods?.[key]?.requireTransactionId)}
                    onChange={(e) => setMethod(key, 'requireTransactionId', e.target.checked)}
                  />
                  <span className="ts-muted">Require a transaction id</span>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* ------------------------------------------------------ notifications */}
        {/* Topup messages moved to the Message Center, next to withdraw,
            convert and purchase, where each one also picks WhatsApp/SMS order. */}
        <div className="ts-card ts-wide">
          <div className="ts-card-head">Notifications</div>
          <div className="ts-body">
            <p style={{ margin: 0 }}>
              Topup messages (received, verified, approved, rejected, cancelled, shared) are now
              managed in the <Link to="/admin/messages">Message Center</Link>, with WhatsApp and SMS
              texts and channel order for each.
            </p>
          </div>
        </div>

        {/* ------------------------------------------------------- supervisors */}
        <div className="ts-card ts-wide">
          <div className="ts-card-head">Supervisors</div>
          <div className="ts-body">
            <label className="ts-switch">
              <input
                type="checkbox"
                checked={form.sharing?.enabled}
                onChange={(e) => set('sharing', { ...form.sharing, enabled: e.target.checked })}
              />
              <span><b>Supervisors can share their shopping balance downward</b></span>
            </label>
            <label className="ts-switch">
              <input
                type="checkbox"
                checked={form.sharing?.wholeDownline !== false}
                onChange={(e) => set('sharing', { ...form.sharing, wholeDownline: e.target.checked })}
              />
              <span>
                Anywhere in their downline
                <div className="ts-muted">
                  Off restricts them to members placed directly under them.
                </div>
              </span>
            </label>
            <label className="ts-label">Smallest transfer (Tk)</label>
            <input
              className="ts-input"
              type="number"
              value={form.sharing?.minPoints ?? 1}
              onChange={(e) => set('sharing', { ...form.sharing, minPoints: e.target.value })}
            />

            <div className="ts-sep" />

            <label className="ts-label">Make someone a supervisor</label>
            <div className="ts-row">
              <input
                className="ts-input"
                placeholder="Username"
                value={lookup}
                onChange={(e) => setLookup(e.target.value)}
                onKeyDown={(e) => (e.key === 'Enter' ? findMember() : null)}
              />
              <button className="ts-btn ts-btn-light" onClick={findMember}>Find</button>
            </div>
            {found ? (
              <div className="ts-found">
                <span>{found.name} (@{found.username}) · {found.phone}</span>
                <button
                  className="ts-btn ts-btn-primary"
                  onClick={() => setSupervisorFlag(found._id, true)}
                >
                  Make supervisor
                </button>
              </div>
            ) : null}

            <div className="ts-label">Current supervisors ({supervisors.length})</div>
            <div className="ts-table-wrap">
              <table className="ts-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Shopping balance</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {supervisors.length === 0 ? (
                    <tr><td colSpan={3} className="ts-muted">Nobody is a supervisor yet.</td></tr>
                  ) : (
                    supervisors.map((s) => (
                      <tr key={s._id}>
                        <td>{s.name} <span className="ts-muted">@{s.username}</span></td>
                        <td>{Number(s.shoppingbalance || 0).toLocaleString()}</td>
                        <td>
                          <button
                            className="ts-linkbtn ts-danger"
                            onClick={() => setSupervisorFlag(s._id, false)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <button className="ts-btn ts-btn-primary ts-save" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save topup settings'}
      </button>
    </div>
  );
}

const CSS = `
.ts-page { width: 87%; float: right; padding: 16px 20px 80px; box-sizing: border-box; }
@media (max-width: 900px) { .ts-page { width: 100%; float: none; padding: 12px; } }
.ts-head { display: flex; justify-content: space-between; gap: 12px; align-items: center; flex-wrap: wrap; }
.ts-head h3 { margin: 0; }
.ts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 14px; align-items: start; }
@media (max-width: 1000px) { .ts-grid { grid-template-columns: 1fr; } }
.ts-wide { grid-column: 1 / -1; }
.ts-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
.ts-card-head { padding: 10px 14px; background: #f9fafb; border-bottom: 1px solid #e5e7eb;
  font-weight: 700; font-size: 12px; text-transform: uppercase; color: #374151; }
.ts-body { padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.ts-label { font-size: 12px; font-weight: 600; color: #374151; }
.ts-input { width: 100%; border: 1px solid #d1d5db; border-radius: 8px; padding: 8px 10px;
  font-size: 14px; box-sizing: border-box; }
.ts-two { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
@media (max-width: 600px) { .ts-two { grid-template-columns: 1fr; } }
.ts-switch { display: flex; gap: 8px; align-items: flex-start; font-size: 13px; }
.ts-switch input { margin-top: 3px; }
.ts-hint { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; border-radius: 8px;
  padding: 8px 10px; font-size: 12px; overflow-wrap: anywhere; }
.ts-method { border: 1px solid #f3f4f6; border-radius: 10px; padding: 10px; display: flex;
  flex-direction: column; gap: 6px; }
.ts-event { border-top: 1px solid #f3f4f6; padding-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.ts-row { display: flex; gap: 8px; flex-wrap: wrap; }
.ts-found { display: flex; justify-content: space-between; align-items: center; gap: 10px;
  background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; font-size: 13px; }
.ts-sep { height: 1px; background: #e5e7eb; margin: 6px 0; }
.ts-muted { color: #6b7280; font-size: 12px; }
.ts-btn { border-radius: 8px; padding: 8px 14px; font-weight: 600; cursor: pointer; font-size: 14px;
  border: 1px solid #d1d5db; background: #fff; text-decoration: none; color: #111827; display: inline-block; }
.ts-btn-primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.ts-btn-light { background: #f3f4f6; }
.ts-save { width: 100%; margin-top: 14px; padding: 12px; font-size: 15px; }
.ts-table-wrap { overflow-x: auto; }
.ts-table { width: 100%; border-collapse: collapse; min-width: 420px; }
.ts-table th { text-align: left; padding: 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb;
  font-size: 11px; text-transform: uppercase; color: #6b7280; }
.ts-table td { padding: 8px; border-bottom: 1px solid #f3f4f6; font-size: 13px; }
.ts-linkbtn { background: none; border: 0; color: #2563eb; cursor: pointer; font-size: 12.5px; }
.ts-preview { background: #f0fdf4; border: 1px solid #bbf7d0; color: #14532d; border-radius: 8px;
  padding: 8px 10px; font-size: 12.5px; line-height: 1.5; }
.ts-danger { color: #b91c1c; }
`;
